import React, { useState, useContext, useEffect } from "react";
import BusinessDataValidation from "../helper/BusinessDataValidation";
import axios from "axios";
import { ConfigContext } from "../context/GlobalContext";
import { useNavigate } from "react-router-dom";
import EditBusinessData from "../data/Firebase/Business/EditBusinessDataInFirebase";
import { Icon } from "@iconify/react/dist/iconify.js";
import { getImage } from "../Utils/image_helper";
import { toast } from "react-toastify";
import CustomerDataValidation from "../helper/CustomerDataValidation";
import { AddCustomer, EditCustomer } from "../data";
import { Languages } from "../Utils/Languages";
import LoaderView from "./LoaderView";
import Country from "../Utils/countriesJson.json";
import GetCustomersData from "../data/Comman/GetCustomersData";
import Currancy from "../Utils/currency.json";
import CountryFlags from "../Utils/countries.json";

const CustomerEditForm = () => {

  const { state, dispatch } = useContext(ConfigContext);

  const navigate = useNavigate();

  const [filteredCountries, setFilteredCountries] = useState(Country);
  const [searchBuffer, setSearchBuffer] = useState("");
  const customer_data = state?.SELECTEDCUSTOMER;
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingGst, setIsFetchingGst] = useState(false);
  const [customerData, setCustomerData] = useState({
    customer: customer_data?.customer,
    customerGSTNo: customer_data?.customerGSTNo,
    phone: customer_data?.phone,
    email: customer_data?.email,
    address: customer_data?.address,
    invoices: customer_data?.invoices,
    srNo: customer_data?.srNo,
    companyId: customer_data?.companyId,
    dialCode: customer_data?.dialCode || customer_data?.dial_code,
  });
  const [countryCode, setCountryCode] = useState(customerData?.dialCode || customerData?.dial_code || "+91");

  // Currency dropdown state
  const [selectedCustomerItem, setSelectedCustomerItem] = useState(null);
  const [currencySearch, setCurrencySearch] = useState("");
  const currencyOptions = Currancy.CountryCurrencyResponse?.map(row => {
    const country = CountryFlags?.CurrencyResponse?.find(c => c.isoAlpha2 === row.countryCode);
    return {
      value: row.currencyCode,
      label: (
        <div className="d-flex align-items-center gap-2">
          <img
            src={country?.flag ? `data:image/png;base64,${country.flag}` : getImage("euro_flag")}
            alt="flag"
            width="25"
            height="15"
          />
          <span>{row.name} ({row.currencyCode} - {row.currencytSymbol})</span>
        </div>
      ),
      raw: row,
    };
  });
  const [filteredCurrencyOptions, setFilteredCurrencyOptions] = useState(currencyOptions);

  // Initialize selected currency from customer data
  useEffect(() => {
    if (customer_data?.currency) {
      const matched = Currancy.CountryCurrencyResponse.find(
        c => c.currencyCode === customer_data.currency
      );
      if (matched) setSelectedCustomerItem(matched);
    }
  }, [customer_data?.currency]);

  const handleCurrencySearch = (e) => {
    const value = e.target.value;
    setCurrencySearch(value);
    const trimmed = value.trim().toLowerCase();
    if (trimmed === "") {
      setFilteredCurrencyOptions(currencyOptions);
    } else {
      const filtered = currencyOptions.filter(opt => {
        const label = String(opt.raw?.name || "").toLowerCase();
        const code = String(opt.value || "").toLowerCase();
        return label.includes(trimmed) || code.includes(trimmed);
      });
      setFilteredCurrencyOptions(filtered);
    }
  };

  const handleCurrencyItemClick = (raw) => {
    setSelectedCustomerItem(raw);
    setCurrencySearch("");
    setFilteredCurrencyOptions(currencyOptions);
  };

  const selectedCurrencyOption = currencyOptions?.find(
    opt => opt.raw?.currencyCode === selectedCustomerItem?.currencyCode
  );

  const [error, setError] = useState({
    customer: null,
    email: null,
    phone: null
  })

  const HandleChangeInBusinessData = (field, value) => {
    setCustomerData((prevData) => ({
      ...prevData,
      [field]: value,
    }));

    setError((prevData) => ({
      ...prevData,
      [field]: null,
    }));
  };

  const fetchGstDetails = async () => {
    if (!customerData.customerGSTNo || customerData.customerGSTNo.length < 15) {
      toast.error("Please enter a valid 15-digit GST number.");
      return;
    }

    setIsFetchingGst(true);

    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}fetch-gst-data/${customerData.customerGSTNo}`);

      if (response.data && response.data.status && response.data.data?.taxpayerInfo) {
        const info = response.data.data.taxpayerInfo;

        const addr = info.pradr?.addr || {};
        const addressParts = [
          addr.bno,
          addr.bnm,
          addr.st,
          addr.loc,
          addr.dst,
          addr.stcd,
        ].filter(part => part && part.toString().trim() !== "");

        let finalAddress = addressParts.join(", ");
        if (addr.pncd) {
          finalAddress += ` - ${addr.pncd}`;
        }

        setCustomerData((prev) => ({
          ...prev,
          customer: info.tradeNam || info.lgnm || prev.customer,
          address: finalAddress || prev.address,
        }));

        toast.success("Customer details fetched successfully!");
      } else {
        toast.error(response.data?.message || "Taxpayer details not found for this GST number.");
      }
    } catch (error) {
      console.error("GST Fetch Error:", error);
      toast.error("Failed to fetch GST details. This GST number might be invalid or the API is unavailable.");
    } finally {
      setIsFetchingGst(false);
    }
  };

  const OnUpdatePress = async () => {

    const { isValid, errors } = CustomerDataValidation(customerData);

    if (!isValid) {
      setError(errors);
      return;
    }

    setIsLoading(true);

    try {
      // original customer name
      const oldName = state?.SELECTEDCUSTOMER?.customer?.toLowerCase()?.trim();
      // new entered name
      const newName = customerData?.customer?.toLowerCase()?.trim();

      const currencyCode = selectedCustomerItem?.currencyCode || customer_data?.currency || "";

      if (oldName !== newName) {
        // 🔹 Name changed → create new customer
        const newCustomerId = Date.now().toString();

        const newCustomer = {
          ...customerData,
          customerId: newCustomerId,
          createdAt: Date.now().toString(),
          updatedAt: Date.now().toString(),
          invoices: [], // start fresh, or copy if needed
          invoiceCount: 0,
          outstanding: "0",
          totalAmount: "0",
          totalPaid: "0",
          currency: currencyCode,
          companyId: (state?.SELECTEDBUSINESS && state?.SELECTEDBUSINESS !== "null") ? state?.SELECTEDBUSINESS : (customerData?.companyId || "null"),
        };
        const actualCompanyId = newCustomer.companyId;
        const selectedBusiness = state?.BUSINESSES?.find(b => String(b.companyId) === String(actualCompanyId));
        const targetUid = selectedBusiness?.isShared ? selectedBusiness.ownerUid : state?.USERID;
        console.log("add customer called", newCustomer);

        // ✅ Save as new customer
        await AddCustomer(state, dispatch, newCustomer, targetUid);
        await GetCustomersData(state, dispatch);
      } else {
        const actualCompanyId = (state?.SELECTEDBUSINESS && state?.SELECTEDBUSINESS !== "null") ? state?.SELECTEDBUSINESS : (customerData?.companyId || "null");
        const selectedBusiness = state?.BUSINESSES?.find(b => String(b.companyId) === String(actualCompanyId));
        const targetUid = selectedBusiness?.isShared ? selectedBusiness.ownerUid : state?.USERID;
        console.log("edit customer called", {
          ...customerData,
          currency: currencyCode,
          updatedAt: Date.now().toString(),
        });

        // 🔹 Same name → update existing customer
        await EditCustomer(state, dispatch, {
          ...customerData,
          currency: currencyCode,
          updatedAt: Date.now().toString(),
        }, targetUid);

        toast.success("Customer updated successfully!");
      }

      navigate(-1);
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  const onBack = async () => {
    navigate(-1);
  }

  const isAUD = selectedCustomerItem?.currencyCode === "AUD" || customer_data?.currency === "AUD" || state?.CURRENCYCODE === "AUD" || state?.TAXTYPE === "ABN";

  return (
    <div className="card shadow-lg p-4 mt-3">
      <div className="card-header">

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          {/* Left Side - Back/Close Button */}
          <div className="d-flex align-items-center gap-2">
            <button
              onClick={() => { navigate(-1) }}
            >
              <i class="fa fa-arrow-left" style={{ fontSize: 18 }}></i>
            </button>
            <p className='fw-semibold mb-0' style={{ fontSize: 20 }}>{Languages[state?.LANGUAGE]?.edit_customer}</p>
          </div>

          {/* Right Side - Other Buttons */}
          <div className="d-flex gap-2">
          </div>
        </div>
      </div>
      <div className="card-body">
        <div className="row">
          <form>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{Languages[state?.LANGUAGE]?.customer_name} <span className="text-danger"> *</span></label>
                <input
                  type="text"
                  className={`form-control wizard-required ${error.customer ? "border-danger" : ""
                    }`}
                  placeholder={Languages[state?.LANGUAGE]?.enter_customer_name}
                  value={customerData.customer}
                  onChange={(e) =>
                    HandleChangeInBusinessData(
                      "customer",
                      e.target.value
                    )
                  }
                />
                {error.customer && (
                  <div className="text-danger small mt-1">
                    {Languages[state?.LANGUAGE]?.customer_name_error}
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{Languages[state?.LANGUAGE]?.phone_number}</label>
                <div className="row g-0">
                  <div className="col-3">
                    <div className="dropdown w-100 h-100">
                      <button
                        className="btn border w-100 text-center d-flex justify-content-center align-items-center h-100"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style={{
                          borderTopLeftRadius: "0.5rem",
                          borderBottomLeftRadius: "0.5rem",
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0
                        }}
                      >

                        {(() => {
                          const selected = Country.find(c => c.dialCode === countryCode);
                          return (
                            <div className="text-secondary-light">
                              <img
                                src={selected?.image}
                                alt="flag"
                                style={{ width: "25px", height: "25px", marginRight: "6px" }}
                              />
                              {countryCode}
                            </div>
                          );
                        })()}
                      </button>
                      <ul
                        className="dropdown-menu w-100 shadow-sm border-0 rounded-0 text-center hide-scrollbar p-1"
                        style={{ minWidth: "110px", padding: 0 }}
                      >
                        <li className="px-2 py-2 sticky-top z-1">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search"
                            value={searchBuffer}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSearchBuffer(value);
                              const trimmed = value.trim().toLowerCase();
                              if (trimmed === "") {
                                setFilteredCountries(Country);
                              } else {
                                const filtered = Country.filter(
                                  (country) =>
                                    country.name.toLowerCase().includes(trimmed) ||
                                    country.dialCode.includes(trimmed)
                                );
                                setFilteredCountries(filtered);
                              }
                            }}
                          />
                        </li>
                        <div className="hide-scrollbar" style={{ maxHeight: "250px", overflowY: "auto" }}>
                          {filteredCountries.length > 0 ? (
                            filteredCountries.map((country) => (
                              <li key={country.id}>
                                <a
                                  className={`dropdown-item  d-flex align-items-center gap-2 px-3 py-2 ${country.dialCode === countryCode ? "bg-primary fw-semibold" : ""
                                    }`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setCountryCode(country.dialCode);
                                    setSearchBuffer("");
                                    setFilteredCountries(Country);
                                    HandleChangeInBusinessData("dialCode", country.dialCode);
                                  }}
                                >
                                  <img
                                    src={country.image}
                                    alt={country.name}
                                    style={{ width: "25px", height: "18px", objectFit: "cover" }}
                                  />
                                  <span className="ms-1 text-secondary-light">{country.dialCode}</span>
                                </a>
                              </li>
                            ))
                          ) : (
                            <li className="text-secondary-light text-center small py-2">No countries found</li>
                          )}
                        </div>
                      </ul>
                    </div>
                  </div>
                  <div className="col-9">
                    <input
                      type="text"
                      className={`form-control w-100 h-100 ${error.phone ? "border-danger" : ""}`}
                      placeholder={Languages[state?.LANGUAGE]?.enter_phone_number}
                      value={customerData.phone}
                      onChange={(e) =>
                        HandleChangeInBusinessData("phone", e.target.value)
                      }
                      style={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderTopRightRadius: "0.5rem",
                        borderBottomRightRadius: "0.5rem"
                      }}
                    />
                  </div>
                </div>

                {error.phone && (
                  <div className="text-danger small mt-1">
                    {Languages[state?.LANGUAGE]?.customer_phone_number_error}
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{Languages[state?.LANGUAGE]?.email}</label>
                <input
                  type="text"
                  className={`form-control wizard-required ${error.email ? "border-danger" : ""
                    }`}
                  placeholder={Languages[state?.LANGUAGE]?.enter_email}
                  value={customerData.email}
                  onChange={(e) =>
                    HandleChangeInBusinessData(
                      "email",
                      e.target.value
                    )
                  }
                />
                {error.email && (
                  <div className="text-danger small mt-1">
                    {Languages[state?.LANGUAGE]?.customer_email_error}
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{isAUD
                  ? "ABN Number"
                  : state?.TAXTYPE === "GST"
                    ? `${Languages[state?.LANGUAGE]?.gst} Number`
                    : `${Languages[state?.LANGUAGE]?.tax} Number`}</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={`Enter ${isAUD ? 'ABN' : (state?.TAXTYPE || 'Tax')} Number`}
                    value={customerData.customerGSTNo}
                    onChange={(e) =>
                      HandleChangeInBusinessData(
                        "customerGSTNo",
                        (state?.TAXTYPE === "GST" && !isAUD) ? e.target.value.toUpperCase() : e.target.value
                      )
                    }
                  />
                  {state?.TAXTYPE === "GST" && !isAUD && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary-600 radius-8 d-inline-flex align-items-center gap-1 px-20"
                      onClick={fetchGstDetails}
                      disabled={isFetchingGst}
                    >
                      {isFetchingGst ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      ) : (
                        <>
                          <Icon icon="solar:globus-search-bold" width="18" />
                          Fetch
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{Languages[state?.LANGUAGE]?.address}</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder={Languages[state?.LANGUAGE]?.enter_address}
                  value={customerData.address}
                  onChange={(e) =>
                    HandleChangeInBusinessData("address", e.target.value)
                  }
                ></textarea>
              </div>

              {/* Currency Dropdown */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Currency</label>
                <div className="dropdown">
                  <button
                    className="btn btn-outline-primary-600 dropdown-toggle w-100 d-flex justify-content-between align-items-center px-16 py-10 rounded"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <span className="d-flex align-items-center gap-2">
                      {selectedCurrencyOption ? (
                        <>
                          {(() => {
                            const flagCountry = CountryFlags?.CurrencyResponse?.find(
                              c => c.isoAlpha2 === selectedCurrencyOption.raw?.countryCode
                            );
                            return (
                              <img
                                src={flagCountry?.flag ? `data:image/png;base64,${flagCountry.flag}` : getImage("euro_flag")}
                                alt="flag"
                                width="25"
                                height="15"
                                style={{ objectFit: "cover", borderRadius: 2 }}
                              />
                            );
                          })()}
                          <span>
                            {selectedCurrencyOption.raw?.name} ({selectedCurrencyOption.raw?.currencyCode} - {selectedCurrencyOption.raw?.currencytSymbol})
                          </span>
                        </>
                      ) : (
                        <>
                          <img
                            src={getImage("euro_flag")}
                            alt="flag"
                            width="25"
                            height="15"
                            style={{ objectFit: "cover", borderRadius: 2 }}
                          />
                          <span>Select Currency</span>
                        </>
                      )}
                    </span>
                    <i className="bi bi-chevron-down"></i>
                  </button>

                  <ul
                    className="dropdown-menu w-100 mt-1 shadow-sm border-0 rounded-3 hide-scrollbar"
                    style={{ padding: 0 }}
                  >
                    <li className="py-2 sticky-top z-2">
                      <input
                        type="text"
                        className="form-control countrybackground"
                        placeholder="Search currency..."
                        value={currencySearch}
                        onChange={handleCurrencySearch}
                      />
                    </li>
                    <li style={{ maxHeight: "250px", overflowY: "auto" }} className="hide-scrollbar">
                      <ul className="list-unstyled mb-0">
                        {filteredCurrencyOptions.length > 0 ? (
                          filteredCurrencyOptions.map(option => (
                            <li key={`${option.raw.countryCode}-${option.value}`}>
                              <a
                                href="#"
                                className={`dropdown-item d-flex align-items-center gap-2 px-16 py-10 text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${selectedCurrencyOption?.value === option.value ? "bg-primary-50 fw-semibold" : ""
                                  }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCurrencyItemClick(option.raw);
                                }}
                              >
                                {option.label}
                                {selectedCurrencyOption?.value === option.value && (
                                  <i className="bi bi-check2 text-primary-600 ms-auto"></i>
                                )}
                              </a>
                            </li>
                          ))
                        ) : (
                          <li className="text-muted text-center small py-2">No currency found</li>
                        )}
                      </ul>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mt-5">
              <div className="d-flex gap-2">
                <button
                  type="reset"
                  onClick={onBack}
                  className="border btn-sm border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-6 radius-8"
                >
                  {Languages[state?.LANGUAGE]?.back}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary-600 radius-8 d-inline-flex align-items-center px-40 py-6 radius-8 gap-2"
                  onClick={OnUpdatePress}
                >
                  {Languages[state?.LANGUAGE]?.update}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      {isLoading && <LoaderView />}
    </div>
  );
};

export default CustomerEditForm;
